import { useEffect, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

interface Section {
  id: string;
  label: string;
}

interface Props {
  sections: Section[];
}

/**
 * Floating sticky right-edge nav. Sits ~20px from the right edge of the viewport.
 * Watches sections via IntersectionObserver and highlights the active one.
 * Clicking a label scrolls the matching section to the top of the viewport.
 *
 * Q-new7 = (a) floating sticky right-edge.
 */
export const SectionMiniNav = ({ sections }: Props) => {
  const theme = useTheme();
  const [active, setActive] = useState<string>(sections[0]?.id ?? '');

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
              setActive(s.id);
            }
          }
        },
        { threshold: [0.3, 0.5, 0.8] },
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [sections]);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(id);
  };

  return (
    <Paper elevation={2} sx={{
      position: 'fixed',
      right: 16,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 156,
      borderRadius: 2,
      backgroundColor: alpha(theme.palette.background.paper, 0.96),
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      backdropFilter: 'blur(4px)',
      zIndex: 10,
      py: 1,
    }}>
      <Typography sx={{
        fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary',
        px: 1.5, mb: 0.5, letterSpacing: 0.5,
      }}>
        SECTIONS
      </Typography>
      {sections.map((s) => {
        const isActive = active === s.id;
        return (
          <Box key={s.id} onClick={() => handleClick(s.id)} sx={{
            cursor: 'pointer',
            py: 0.6, px: 1.5,
            fontSize: '0.78rem',
            fontWeight: isActive ? 700 : 500,
            color: isActive ? 'primary.main' : 'text.primary',
            borderLeft: `3px solid ${isActive ? theme.palette.primary.main : 'transparent'}`,
            backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
          }}>
            {s.label}
          </Box>
        );
      })}
    </Paper>
  );
};
